import { useEffect, useState } from 'react';

import {
  ArrowLeft,
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Phone,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStockListing } from '@/hooks/useDealerKit';

import { DynamicFields } from './stock-search/DynamicFields';
import { classifyValue, countFields, humanizeKey } from './stock-search/formatField';

type Json = Record<string, unknown>;

const obj = (v: unknown): Json =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : {};

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : null;

/** Every http(s) image URL in data.media.images (objects with url, or plain strings). */
const imagesOf = (data: Json): string[] => {
  const list = obj(data.media).images;
  if (!Array.isArray(list)) return [];
  return list
    .map((i) => (typeof i === 'string' ? i : str(obj(i).url)))
    .filter((u): u is string => !!u && /^https?:\/\//.test(u));
};

const show = (key: string, v: unknown) => {
  const value = classifyValue(key, v);
  return value.kind === 'empty' || value.kind === 'group' || value.kind === 'list'
    ? null
    : value.text;
};

/** Connector rows only map name, telephone and postcode; DealerKit rows may carry more. */
const ADDRESS_KEYS = ['address_line_1', 'address', 'street', 'town', 'city', 'county', 'postcode'];
const addressOf = (location: Json): string =>
  ADDRESS_KEYS.map((k) => str(location[k]))
    .filter(Boolean)
    .join(', ');

const Gallery = ({ images, title }: { images: string[]; title: string }) => {
  const [index, setIndex] = useState(0);
  if (!images.length) {
    return (
      <div className="bg-muted text-muted-foreground flex aspect-[16/10] items-center justify-center rounded-lg">
        <Car className="h-12 w-12" aria-hidden />
      </div>
    );
  }
  const go = (d: number) => setIndex((i) => (i + d + images.length) % images.length);
  return (
    <div className="space-y-2">
      <div
        className="bg-muted focus-visible:ring-ring relative aspect-[16/10] overflow-hidden rounded-lg focus-visible:ring-2 focus-visible:outline-none"
        tabIndex={0}
        role="group"
        aria-label={`Photo ${index + 1} of ${images.length}`}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') go(-1);
          if (e.key === 'ArrowRight') go(1);
        }}
      >
        <img
          src={images[index]}
          alt={`${title} — photo ${index + 1}`}
          className="h-full w-full object-cover"
        />
        {images.length > 1 && (
          <>
            <Button
              size="icon"
              variant="secondary"
              aria-label="Previous photo"
              className="absolute top-1/2 left-2 -translate-y-1/2 opacity-90"
              onClick={() => go(-1)}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              aria-label="Next photo"
              className="absolute top-1/2 right-2 -translate-y-1/2 opacity-90"
              onClick={() => go(1)}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={src + i}
              type="button"
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === index}
              onClick={() => setIndex(i)}
              className={`h-14 w-20 shrink-0 cursor-pointer overflow-hidden rounded border-2 transition-colors ${i === index ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'}`}
            >
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Facts = ({ items }: { items: [string, string | null][] }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
    {items
      .filter(([, v]) => v)
      .map(([k, v]) => (
        <div key={k}>
          <dt className="text-muted-foreground text-xs">{k}</dt>
          <dd className="text-sm font-medium">{v}</dd>
        </div>
      ))}
  </dl>
);

const titleOf = (data: Json): string => {
  const vehicle = obj(data.vehicle);
  return (
    [vehicle.manufacturer, vehicle.model, vehicle.derivative ?? vehicle.trim]
      .map(str)
      .filter(Boolean)
      .join(' ') || 'Unnamed vehicle'
  );
};

export const StockCarDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: row, isLoading, error } = useStockListing(id);
  const [copied, setCopied] = useState<string | null>(null);

  const title = row ? titleOf(obj(row.data)) : 'Car';

  useEffect(() => {
    document.title = `${title} — Stock`;
  }, [title]);

  const copy = async (label: string, text: string) => {
    try {
      if (!navigator.clipboard) return;
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard unavailable or denied: nothing to show.
    }
  };

  const back = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() =>
        window.history.length > 1 ? navigate(-1) : navigate('/crm-integration/stock')
      }
    >
      <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
      Back to results
    </Button>
  );

  if (isLoading) {
    return (
      <div className="space-y-4 p-6" aria-busy="true">
        {back}
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="aspect-[16/10] w-full" />
          <div className="space-y-3">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !row) {
    const notFound = (error as { response?: { status?: number } } | null)?.response?.status === 404;
    return (
      <div className="space-y-4 p-6">
        {back}
        <div role="alert" className="rounded-lg border border-dashed p-10 text-center">
          <p className="font-medium">
            {notFound ? 'This car is no longer in stock' : 'Could not load this car'}
          </p>
          <Link
            to="/crm-integration/stock"
            className="text-primary mt-2 inline-block text-sm hover:underline"
          >
            Back to the search
          </Link>
        </div>
      </div>
    );
  }

  const data = obj(row.data);
  const vehicle = obj(data.vehicle);
  const prices = obj(data.prices);
  const advertised = obj(prices.advertised);
  const monthly = obj(prices.monthly);
  const location = obj(data.location);
  const price = show('price', advertised.amount);
  const websiteRaw = str(obj(data.links).website);
  const website = websiteRaw && /^https?:[/][/]/i.test(websiteRaw) ? websiteRaw : null;
  const phone = str(location.telephone);
  const address = addressOf(location);
  const mileage = show('mileage', vehicle.mileage);
  const fieldCount = countFields(data);

  return (
    <div className="space-y-6 p-6">
      <span className="sr-only" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {back}
        <Button variant="outline" size="sm" onClick={() => void copy('link', window.location.href)}>
          {copied === 'link' ? (
            <Check className="mr-1 h-4 w-4" aria-hidden />
          ) : (
            <Copy className="mr-1 h-4 w-4" aria-hidden />
          )}
          Copy link
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Gallery key={row.id} images={imagesOf(data)} title={title} />
        <div className="space-y-4">
          <div>
            <p className="text-3xl font-bold">{price ?? 'Price on request'}</p>
            {str(advertised.vat_status) && (
              <p className="text-muted-foreground text-sm">VAT: {str(advertised.vat_status)}</p>
            )}
            <h1 className="mt-1 text-xl font-semibold">{title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {row.status && (
              <Badge variant="secondary" className="capitalize">
                {row.status}
              </Badge>
            )}
            <Badge variant="outline">{row.connector_name ?? 'DealerKit'}</Badge>
            {row.registration && (
              <button
                type="button"
                onClick={() => void copy('reg', row.registration as string)}
                className="bg-muted focus-visible:ring-ring inline-flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 font-mono text-sm focus-visible:ring-2"
                aria-label={`Copy registration ${row.registration}`}
              >
                {row.registration}
                {copied === 'reg' ? (
                  <Check className="h-3 w-3" aria-hidden />
                ) : (
                  <Copy className="h-3 w-3" aria-hidden />
                )}
              </button>
            )}
          </div>
          <Facts
            items={[
              ['Year', show('year', vehicle.year)],
              ['Mileage', mileage ? `${mileage} mi` : null],
              ['Fuel', show('fuel', vehicle.fuel_type)],
              ['Gearbox', show('transmission', vehicle.transmission)],
              ['Body', show('body', vehicle.body_type)],
              ['Engine', show('engine', vehicle.engine_size)],
              ['Owners', show('owners', data.owners)],
              ['Colour', show('colour', vehicle.colour)],
            ]}
          />
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="all">All details ({fieldCount})</TabsTrigger>
          <TabsTrigger value="json">Raw JSON</TabsTrigger>
          <TabsTrigger value="sync">Sync</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Price &amp; finance</CardTitle>
            </CardHeader>
            <CardContent>
              <Facts
                items={Object.entries({
                  ...advertised,
                  ...Object.fromEntries(
                    Object.entries(monthly).map(([k, v]) => [`monthly_${k}`, v]),
                  ),
                }).map(([k, v]) => [humanizeKey(k), show(k, v)])}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Dealer &amp; location</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="font-medium">{str(location.name) ?? 'Unknown dealer'}</p>
              {address && <p className="text-muted-foreground">{address}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                {phone && (
                  <Button asChild variant="outline" size="sm">
                    <a href={`tel:${phone}`}>
                      <Phone className="mr-1 h-4 w-4" aria-hidden />
                      {phone}
                    </a>
                  </Button>
                )}
                {website && (
                  <Button asChild variant="outline" size="sm">
                    <a href={website} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-4 w-4" aria-hidden />
                      Dealer page
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          {str(data.notes) && (
            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{str(data.notes)}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <DynamicFields data={data} />
        </TabsContent>

        <TabsContent value="json" className="mt-4 space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void copy('json', JSON.stringify(row.data ?? {}, null, 2))}
          >
            {copied === 'json' ? (
              <Check className="mr-1 h-4 w-4" aria-hidden />
            ) : (
              <Copy className="mr-1 h-4 w-4" aria-hidden />
            )}
            Copy JSON
          </Button>
          <pre className="bg-muted max-h-[600px] overflow-auto rounded-md p-3 font-mono text-xs">
            {JSON.stringify(row.data ?? {}, null, 2)}
          </pre>
        </TabsContent>

        <TabsContent value="sync" className="mt-4">
          <Facts
            items={[
              ['Source', row.connector_name ?? 'DealerKit'],
              ['Stock listing ID', row.stock_listing_id],
              ['VIN', row.vin],
              ['Synced', new Date(row.synced_at).toLocaleString('en-GB')],
              [
                'Last seen',
                row.last_seen_at ? new Date(row.last_seen_at).toLocaleString('en-GB') : null,
              ],
              ['Created', new Date(row.created_at).toLocaleString('en-GB')],
              ['Updated', new Date(row.updated_at).toLocaleString('en-GB')],
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
